import {useAlert, useAuth} from "../../context";
import React, {useState} from "react";
import {Alert, Button, Modal, Stack} from "react-bootstrap";
import {ResponsiveContent} from "../ResponsiveNavTabs";
import {Link, useNavigate} from "react-router-dom";
import {Formik, Form as FormikForm} from "formik";
import {CheckText, ColumnizeText, CustomRecaptcha, PageTitle, PasswordButton, SingleText} from "../_components";
import * as yup from "yup";
import {regex} from "../../utils2";
import {RenderMarkdownGitHub} from "../../markdown/utils/utilsForMarkdown";


export function Signup() {
    return <ResponsiveContent body={<SignupForm/>}/>
}

function SignupForm() {
    const navigate = useNavigate();
    const {success, warn, error} = useAlert();
    const {signup} = useAuth();

    const validationSchema = yup.object().shape({
        first_name: yup.string()
            .required("First name is missing."),
        last_name: yup.string()
            .required("Last name is missing."),
        username: yup.string()
            .required("Email is mandatory.")
            .email("Email not valid"),
        password: yup.string()
            .required("Password is mandatory.")
            .matches(regex.password_pattern, " Password must be at least 8 characters long and include a number, uppercase, lowercase, and special character."),
        accept: yup.boolean().oneOf([true], "You must accept the terms and conditions."),
        recaptcha: yup.string().required("Please complete the reCAPTCHA verification")
    })

    const value = {
        first_name: "",
        last_name: "",
        location: "",
        username: "",
        password: "",
        accept: false,
        recaptcha: "",
    }

    function handleSubmit(values, actions) {
        console.log(values)
        signup(values)
            .then(resp => {
                if (resp.success) {
                    navigate("/account/login");
                    success(<p>An email was sent to <i>{values.username}</i> with a validation link. Check your email and <Alert.Link as={Link}
                                                                                                                                      to="/account/login"> login</Alert.Link>.
                    </p>);

                    if ("debug" in resp) {
                        const to = "/account/verify-email?token=" + resp.debug?.jwt
                        success(<p><Alert.Link as={Link} to={to}>verify</Alert.Link></p>, "Verification!", {autoClose: false});
                    }
                } else if (resp.message.includes("already registered")) {
                    warn(<p>Have you forgotten your password? <Alert.Link as={Link} to="/account/forgot-password">Reset your password</Alert.Link> or register with a
                        different email.</p>);
                } else {
                    error(resp.message, "Server error");
                }
            })
            .catch(console.warn)
            .finally(() => actions.setSubmitting(false))
    }

    return (
        <>
            <PageTitle title="Signup">Type your credentials to create an account.</PageTitle>

            <Formik validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={value} enableReinitialize={true}>
                {({isSubmitting, resetForm, setFieldValue, errors, touched}) => (
                    <FormikForm>

                        <ColumnizeText>
                            <SingleText name="first_name" label="Name" maxLength="64"/>
                            <SingleText name="last_name" label="Surname" maxLength="64"/>
                        </ColumnizeText>

                        <SingleText name="location" type="textarea" label="Biography (Optional)"/>

                        <ColumnizeText className="">
                            <SingleText name="username" label="Email" maxLength="64" autoComplete="username"/>
                            <PasswordButton name="password" label="Password" position="right" autoComplete="new-password"/>
                        </ColumnizeText>

                        <CheckText name="accept" label={<Conditions setFieldValue={setFieldValue}/>} className="mt-4"/>

                        <div className="mt-4"/>
                        <div>Didn't receive the verification email? <Link to="/account/resend-email"
                                                                          className="flex-shrink-0 link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">Resend
                            it</Link></div>

                        <div className="py-2 d-flex justify-content-end align-items-end flex-column">
                            <div>
                                <CustomRecaptcha onChange={(value) => setFieldValue("recaptcha", value)}/>
                            </div>
                            <div>
                                {errors.recaptcha && touched.recaptcha && (
                                    <div className="text-danger">{errors.recaptcha}</div>
                                )}
                            </div>
                        </div>

                        <Stack direction="horizontal" gap="2" className="py-3 justify-content-end align-items-center">
                            <Button type="reset" variant="secondary" onClick={resetForm}>Reset</Button>
                            <Button type="submit" variant="primary" disabled={isSubmitting}>Create Account</Button>
                        </Stack>

                    </FormikForm>
                )}
            </Formik>

        </>
    )
}


function Conditions({setFieldValue}) {
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);

    function handleCheck() {
        setShow(false)
        setFieldValue("accept", true);
    }

    function handleShow() {
        setShow(true)
    }

    return (
        <>
            <span>I confirm I have read and agree with <Link to="#" onClick={handleShow}>terms and conditions</Link></span>

            <Modal
                show={show}
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Terms and Conditions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/*<RenderMarkdown path="/text/TermsAndConditionsAccount.md"/>*/}
                    <RenderMarkdownGitHub github="https://raw.githubusercontent.com/altrabits/AB_Documents/main/SiteInfo/TermsAndConditionsAccount.md"/>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleCheck}>Agree</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
